import React, { useState, useEffect, useCallback } from 'react';
import { Alert, Spinner } from 'react-bootstrap';
import checkinMessageApi from '../services/checkinMessageApi';
import { formatDateTime } from '../utils/dateFormatter';
// Arabic-Indic digits. These counters used to render Latin, which was invisible on the booking page
// and became obvious the moment the Inc 7 panel put them beside a `formatCount` counter of its own.
import { formatCount } from '../utils/numberFormatter';
import '../styles/BookingCheckinMessagesSection.css';

// Read-only "automated messages" section on the booking-detail page: shows what EVERY automated
// guest-message pipeline did for this booking — the text we sent, or why it was skipped/failed,
// plus status + timestamp. No editing, no resend, no composer (deliberately read-only).
//
// Covers BOTH automations, which share one ledger (checkin_message_sends) keyed by messageType:
//   • check-in message + its photos — tasks/checkin-message-automation.md §7
//   • checkout reminder            — tasks/checkout-message-automation.md §3.8
// The file keeps its original name for continuity; the section is no longer check-in-only.

const STATUS_LABELS = {
  sent: 'تم الإرسال',
  pending: 'قيد الإرسال',
  failed: 'فشل الإرسال',
  skipped: 'لم تُرسل',
  waiting: 'بانتظار رمز القفل'
};

const SKIP_REASON_LABELS = {
  no_ota_thread: 'لا توجد محادثة على المنصة (حجز مباشر / جاذر ان)',
  cancelled: 'حجز ملغى',
  overridden: 'تم تعطيل الأتمتة لهذا الحجز',
  photo_window_expired: 'انتهت مهلة إرسال الصورة (٢٤ ساعة بعد الرسالة النصية)',
  // The guest was moved to another unit after the text went out, so the remaining photos would have
  // shown a building they are no longer going to. Registered here because this row DOES render its
  // skip reason — an unregistered value falls through to the raw key, i.e. an English word inside
  // an RTL Arabic card.
  photo_unit_changed: 'تم نقل الضيف إلى وحدة أخرى — أُوقف إرسال بقية الصور',
  // Checkout-only. The send window is 20 minutes wide, so a missed one is never sent late —
  // this row IS the record that a guest did not get their reminder.
  missed_window: 'فات وقت الإرسال — لم يستلم الضيف التذكير',
  already_departed: 'انتهى الحجز قبل موعد الإرسال',
  // Suppressed on purpose: the team had already recorded this guest's departure when the reminder
  // came due. Distinct from `already_departed` above, where there was never a chance to send.
  checkout_recorded: 'سُجّل خروج الضيف — لا حاجة للتذكير',
  // «رسالة الاطمئنان» (tasks/stay-check-message.md). A skip is final for that message.
  guest_contact: 'تواصل الضيف معنا بعد دخوله — لا حاجة للرسالة',
  open_complaint: 'توجد شكوى مفتوحة على الحجز',
  open_request: 'يوجد طلب ضيف مفتوح على الحجز',
  no_entry_detected: 'لم يُسجَّل دخول الضيف بالرمز الخاص بحجزه',
  entry_not_detectable: 'لا يمكن رصد دخول الضيف لهذه الوحدة (لا قفل أو لا رمز خاص بالحجز)',
  missed_send_window: 'فات وقت الإرسال — لم يستلم الضيف رسالة الاطمئنان',
  arrived_before_enabled: 'دخل الضيف قبل تفعيل الرسالة لهذه الوحدة',
  departure_recorded: 'سُجّل خروج الضيف قبل موعد الإرسال'
};

const ERROR_LABELS = {
  code_failed: 'فشل إنشاء رمز القفل الذكي — يحتاج مراجعة',
  code_stuck: 'تأخّر إنشاء رمز القفل الذكي — يحتاج مراجعة',
  send_failed: 'تعذّر إرسال الرسالة — ستتم إعادة المحاولة',
  ota_copy_missing: 'نسخة الصورة الخاصة بالمنصة غير موجودة — أعد رفع الصورة من صفحة العقار',
  photo_send_failed: 'تعذّر إرسال الصورة — ستتم إعادة المحاولة',
  no_destination: 'لا توجد قناة للتواصل مع الضيف'
};

// Every row is labelled by type now that the section carries more than one automation — leaving
// the text rows unlabelled would make «تم الإرسال» ambiguous between check-in and checkout.
// Order follows the guest's journey: check-in → its photos → stay check → checkout.
// Arrow icons use the LEFT variants deliberately: Bootstrap Icons are glyphs and are NOT mirrored by
// the RTL stylesheet, so the "-right" versions point against the reading direction here.
const TYPE_META = {
  checkin:         { label: 'رسالة تسجيل الدخول', icon: 'bi-box-arrow-in-left', order: 0 },
  checkin_photo_1: { label: 'صورة واجهة المبنى',  icon: 'bi-image',             order: 1 },
  checkin_photo_2: { label: 'صورة مدخل العقار',   icon: 'bi-image',             order: 2 },
  stay_check:      { label: 'رسالة الاطمئنان',    icon: 'bi-chat-heart',        order: 3 },
  checkout:        { label: 'تذكير المغادرة',     icon: 'bi-box-arrow-left',    order: 4 }
};
// Forward-compatible fallback: a row type this build doesn't know (a future automation) must still
// be labelled — an unlabelled row shows a bare «تم الإرسال» and reintroduces exactly the ambiguity
// TYPE_META exists to remove. Never render the raw English messageType to an Arabic user.
const UNKNOWN_TYPE = { label: 'رسالة آلية', icon: 'bi-envelope', order: 9 };
const typeMeta = (messageType) => TYPE_META[messageType] || UNKNOWN_TYPE;

/**
 * @param {object} [booking]    the booking-details page passes the whole document (original prop)
 * @param {string} [bookingId]  the Inc 7 inbox context panel passes the id ALONE — it never fetches
 *                              a booking, and this section only ever needed the id
 *
 * Additive on purpose: the id form was added for the second consumer without touching the first
 * one's call site. Nothing here reads any other field of `booking`, so the two are equivalent.
 *
 * @param {'h2'|'h3'} [headingLevel='h2']  the booking page renders this among sibling h2 card
 *   titles, so h2 is right there. Inside the inbox panel it is nested under an h3, where a hard
 *   h2 inverted the document outline (h2 → h3 → h2) and, because `.cms-title` is 1.3rem, made
 *   the nested section's title visibly LARGER than the one above it.
 */
const BookingCheckinMessagesSection = ({ booking, bookingId: bookingIdProp, headingLevel = 'h2' }) => {
  const Heading = headingLevel === 'h3' ? 'h3' : 'h2';
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const bookingId = bookingIdProp || booking?._id;

  const fetchMessages = useCallback(async () => {
    if (!bookingId) return;
    setLoading(true);
    setError(null);
    try {
      const result = await checkinMessageApi.list(bookingId);
      if (result?.success) {
        setMessages(result.messages || []);
      } else {
        setError(result?.error || 'حدث خطأ في جلب الرسائل');
      }
    } catch (err) {
      const backendMsg = err.response?.data?.error || err.response?.data?.message;
      setError(backendMsg || 'حدث خطأ في جلب الرسائل');
    } finally {
      setLoading(false);
    }
  }, [bookingId]);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  return (
    <div className="details-card checkin-messages-section" dir="rtl">
      <div className="cms-header">
        <Heading className="cms-title">
          <i className="bi bi-envelope-paper cms-title-icon"></i>
          الرسائل الآلية
          {!loading && messages.length > 0 && (
            <span className="cms-count">{formatCount(messages.length)}</span>
          )}
        </Heading>
      </div>

      {loading && (
        <div className="cms-loading">
          <Spinner animation="border" size="sm" />
          <span>جاري التحميل...</span>
        </div>
      )}

      {!loading && error && (
        <Alert variant="danger" className="cms-error">{error}</Alert>
      )}

      {!loading && !error && messages.length === 0 && (
        <div className="cms-empty">
          <i className="bi bi-envelope cms-empty-icon"></i>
          <p>لم تُرسل رسائل آلية لهذا الحجز بعد</p>
        </div>
      )}

      {!loading && !error && messages.length > 0 && (() => {
        // Show the version tag on EVERY row of a type that has more than one version — tagging only
        // «نسخة 2» left the v1 row beneath it unlabelled and indistinguishable from a single send.
        const seen = new Map();
        messages.forEach((m) => seen.set(m.messageType, Math.max(seen.get(m.messageType) || 1, m.version || 1)));
        const versionedTypes = new Set([...seen.entries()].filter(([, v]) => v > 1).map(([t]) => t));
        return (
        <div className="cms-list">
          {[...messages]
            // Group by type, then newest version first WITHIN a type. Without the explicit
            // tie-break the order silently depended on the API's sort, which put v2 above an
            // unlabelled v1 — two rows that looked identical in the wrong order.
            .sort((a, b) =>
              (typeMeta(a.messageType).order - typeMeta(b.messageType).order) ||
              ((b.version || 1) - (a.version || 1)) ||
              (new Date(b.createdAt) - new Date(a.createdAt))
            )
            .map((m) => (
            <div key={m._id} className={`cms-row cms-row--${m.status}`}>
              <div className="cms-row__head">
                <span className="cms-type">
                  <i className={`bi ${typeMeta(m.messageType).icon}`}></i> {typeMeta(m.messageType).label}
                </span>
                <span className={`cms-status cms-status--${m.status}`}>
                  {STATUS_LABELS[m.status] || m.status}
                </span>
                {versionedTypes.has(m.messageType) && <span className="cms-version">نسخة {formatCount(m.version || 1)}</span>}
                {/* Only a `sent` row has a delivery time. On skipped/failed/waiting rows this is the
                    row's own timestamp, so label it — «لم تُرسل» beside a bare time reads as a
                    send time, which is the opposite of what happened. */}
                <span className="cms-timestamp">
                  {m.status !== 'sent' && 'سُجّل: '}{formatDateTime(m.sentAt || m.createdAt)}
                </span>
              </div>

              {m.status === 'skipped' && (
                <p className="cms-reason">
                  <i className="bi bi-info-circle"></i>{' '}
                  {SKIP_REASON_LABELS[m.skipReason] || m.skipReason || 'لم تُرسل'}
                </p>
              )}

              {m.status === 'failed' && (
                <p className="cms-reason cms-reason--error">
                  <i className="bi bi-exclamation-triangle"></i>{' '}
                  {ERROR_LABELS[m.error] || m.error || 'فشل الإرسال'}
                </p>
              )}

              {m.renderedText && (m.status === 'sent' || m.status === 'failed') && (
                <pre className="cms-text">{m.renderedText}</pre>
              )}

              {m.photoUrl && m.status === 'sent' && (
                <img src={m.photoUrl} alt={typeMeta(m.messageType).label} className="cms-photo-thumb" />
              )}
            </div>
          ))}
        </div>
        );
      })()}
    </div>
  );
};

export default BookingCheckinMessagesSection;
